import { usePaywall } from '@/hooks/use-paywall';
import { ContactHelpLink } from '@/components/ContactHelpLink';
import { BillingModalFrame } from '@/components/subscription/billing-modal/BillingModalFrame';
import { ShopifyStoreConnect } from '@/components/subscription/billing-modal/ShopifyStoreConnect';
import { PlanChoicePanel } from '@/components/subscription/plan-choice/PlanChoicePanel';
import {
  PLAN_CHOICE_IDS,
  isActivePaidSubscription,
} from '@/components/subscription/plan-choice/plan-choice-offer';
import { useAuth } from '@/contexts/auth-context';
import { getMcpReturnTo } from '@/libs/mcp-return-to';
import { usePlanChoiceStore } from '@/stores/plan-choice-store';
import { Button, Flex, Link, Stack } from '@chakra-ui/react';
import { PlanTier, type OrganizationBillingContext, type User } from '@fluenceur/types';
import { NoticePanel } from '@fluenceur/ui';
import { parseAsStringLiteral, useQueryState } from 'nuqs';
import { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { OnboardingBrandSwitch } from '../_components/OnboardingBrandSwitch';
import { BillingCountryStep } from './BillingCountryStep';
import { BillingEmailStep } from './BillingEmailStep';
import { BillingCardForm } from './BillingCardForm';
import { BillingPaidPlanForm } from './BillingPaidPlanForm';
import { isPaidCheckoutPending } from '@/libs/billing-access';

/** The two account ways out, as discreet buttons at the top right of the plan choice header. */
export function BillingAccountExits({ canReadBilling }: { canReadBilling: boolean }) {
  const { t } = useTranslation();
  const exit = (href: string, label: string) => (
    <Button
      asChild
      variant="outline"
      size="xs"
      h="32px"
      px="12px"
      borderRadius="full"
      borderColor="rgba(26, 26, 26, 0.18)"
      color="ink"
      _hover={{ bg: 'whiteAlpha.500' }}
    >
      <a href={href}>{label}</a>
    </Button>
  );
  return (
    <Flex gap="8px" wrap="wrap">
      {canReadBilling && exit('/payments/commissions', t('onboarding.billing.financialLink'))}
      {exit('/settings/account/profile', t('onboarding.billing.accountLink'))}
    </Flex>
  );
}

/**
 * Every way out of a billing step that cannot be dismissed. Under the plan
 * choice the account exits sit in its header, so only the rest is listed here.
 */
export function BillingExits({
  canReadBilling,
  accountLinks = true,
}: {
  canReadBilling: boolean;
  accountLinks?: boolean;
}) {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const signOut = (
    <Button
      variant="plain"
      onClick={() => void logout().then(() => navigate('/', { replace: true }))}
    >
      {t('onboarding.billing.logout')}
    </Button>
  );
  if (!accountLinks)
    return (
      <Stack
        mt="24px"
        pt="12px"
        borderTopWidth="1px"
        borderColor="border.subtle"
        align="center"
        gap={0}
      >
        <Flex gap="16px" align="center" justify="center" wrap="wrap">
          <ContactHelpLink />
          {signOut}
        </Flex>
        <OnboardingBrandSwitch />
      </Stack>
    );
  return (
    <Stack gap={3} mt={6}>
      {canReadBilling && (
        <Link href="/payments/commissions">{t('onboarding.billing.financialLink')}</Link>
      )}
      <Link href="/settings/account/profile">{t('onboarding.billing.accountLink')}</Link>
      <ContactHelpLink />
      <OnboardingBrandSwitch />
      {signOut}
    </Stack>
  );
}

export function BillingOnboardingContent({
  user,
  context,
  refresh,
  unavailable = false,
  onClose,
  dismissible = true,
}: {
  user: User;
  context: OrganizationBillingContext;
  refresh: () => Promise<unknown>;
  unavailable?: boolean;
  onClose?: () => void;
  /** False while the organization has no access: the modal offers its exits instead of closing. */
  dismissible?: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const close = onClose ?? (() => navigate('/settings/billing'));
  const [selected, setSelected] = useQueryState(
    'plan',
    parseAsStringLiteral(PLAN_CHOICE_IDS).withDefault('free')
  );
  const [freeConfirmed, setFreeConfirmed] = useState(false);
  const markDecided = usePlanChoiceStore((state) => state.markDecided);
  const [params] = useSearchParams();
  const { origin } = usePaywall();
  const returnTo = getMcpReturnTo(params.get('returnTo')) ?? getMcpReturnTo(origin);
  const paidIntent = selected !== 'free';
  const canPaidCheckout =
    context.checkoutAvailability.paid &&
    context.taxQualificationRequired === false &&
    context.cardSetup.exemption !== 'impersonation';
  const needsPaidDetails =
    context.taxQualificationRequired === true ||
    (canPaidCheckout && context.cardSetup.identityRequired);
  const [paidDetailsStarted, setPaidDetailsStarted] = useState(paidIntent && needsPaidDetails);
  useEffect(() => {
    if (!paidIntent) setPaidDetailsStarted(false);
    else if (needsPaidDetails) setPaidDetailsStarted(true);
  }, [paidIntent, needsPaidDetails]);
  const country = user.currentBrand?.companyDetails?.country ?? '';
  const needsCountry = context.billingCurrency === null;
  const chooseFree =
    context.effectiveTier === PlanTier.FREE && !context.cardSetup.operation && !freeConfirmed;
  const planChoice = () => (
    <PlanChoicePanel
      isOpen
      context={context.access.allowed ? 'upgrade' : 'onboarding'}
      selectedPlan={selected}
      onSelectPlan={(plan) => {
        void setSelected(plan);
      }}
      onDecided={close}
      onConfirmFree={() => {
        markDecided(context.brandId);
        if (context.cardSetup.requirement !== 'required') close();
        else setFreeConfirmed(true);
      }}
      catalogCurrency={context.billingCurrency ?? undefined}
      paidAvailable={
        !unavailable &&
        (context.channel === 'shopify' ||
          context.checkoutAvailability.paid ||
          (context.channel === 'stripe' && isActivePaidSubscription(context.subscription))) &&
        context.capabilities.canCommitPaidPlan
      }
      paidUnavailableMessage={t('onboarding.billing.paidUnavailable')}
      freeBillingSummary={t('onboarding.billing.freeSummary')}
      billingContext={context}
      refreshBilling={refresh}
      dismissible={dismissible}
      headerActions={
        dismissible ? undefined : (
          <BillingAccountExits canReadBilling={context.capabilities.canReadBilling} />
        )
      }
      exits={
        dismissible ? undefined : (
          <BillingExits canReadBilling={context.capabilities.canReadBilling} accountLinks={false} />
        )
      }
    />
  );
  if (context.access.reason === 'shopify_store_required')
    return (
      <BillingModalFrame onClose={close} dismissible={dismissible}>
        <ShopifyStoreConnect />
        <BillingExits canReadBilling={context.capabilities.canReadBilling} />
      </BillingModalFrame>
    );
  if (
    context.channel === 'shopify' &&
    context.shopifyFreeApproval &&
    ['required', 'pending'].includes(context.shopifyFreeApproval.requirement)
  )
    return planChoice();
  if (context.cardSetup.requirement !== 'required' && !paidIntent) return planChoice();
  let body: ReactNode;
  if (paidIntent ? !context.capabilities.canCommitPaidPlan : !context.capabilities.canSetupCard) {
    body = (
      <NoticePanel
        tone="info"
        title={t(paidIntent ? 'planChoice.paid.memberTitle' : 'onboarding.billing.memberTitle')}
      >
        {t(paidIntent ? 'planChoice.paid.memberBody' : 'onboarding.billing.memberBody')}
      </NoticePanel>
    );
  } else if (!paidIntent && !context.checkoutAvailability.freeCardSetup) {
    body = <NoticePanel tone="warning" title={t('onboarding.billing.setupUnavailable')} />;
  } else if (
    needsCountry &&
    (!paidIntent || context.checkoutAvailability.freeCardSetup || context.checkoutAvailability.paid)
  ) {
    body = <BillingCountryStep initialCountry={country} refresh={refresh} paid={paidIntent} />;
  } else if (
    paidIntent &&
    context.channel === 'stripe' &&
    context.cardSetup.exemption !== 'impersonation' &&
    context.cardSetup.emailVerificationRequired
  ) {
    body = <BillingEmailStep refresh={refresh} />;
  } else if (
    paidIntent &&
    context.channel === 'stripe' &&
    (needsPaidDetails || paidDetailsStarted) &&
    context.cardSetup.exemption !== 'impersonation' &&
    !isPaidCheckoutPending(context)
  ) {
    body = (
      <BillingPaidPlanForm
        user={user}
        context={context}
        plan={selected}
        refresh={refresh}
        unavailable={unavailable}
        {...(returnTo ? { returnTo } : {})}
        onCompare={() => {
          void setSelected('free');
        }}
      />
    );
  } else if (chooseFree || paidIntent) {
    return planChoice();
  } else if (context.cardSetup.emailVerificationRequired) {
    body = <BillingEmailStep refresh={refresh} />;
  } else {
    body = <BillingCardForm user={user} context={context} refresh={refresh} />;
  }
  return (
    <BillingModalFrame onClose={close} dismissible={dismissible}>
      {body}
      <BillingExits canReadBilling={context.capabilities.canReadBilling} />
    </BillingModalFrame>
  );
}
